// demoStimulation — S6 of docs/PLAN_permian_demo_corpus.md. Authors the stimulation
// half of the Permian pad: a "fracking" job per well, the treatment stream on each, and
// a pad-level shared-services stream for the wireline, plugs and equipment the wells
// take turns on.
//
// ── TWO STREAMS, because the screen has two bands ────────────────────────────────────
// `COMMON_LANE_IDS` (constants.ts:174) routes weather · perf · pumpdown · plug · coil ·
// flowback · equipment · hse · tcp · crane to the PAD band, which reads its own
// pad-scoped store (`STIMULATION_PAD_STORAGE_PREFIX + padId`, entries stamped
// `wellboreId = padId` with the serviced well in `targetWellboreId`). Only `lane-frac`
// renders on a WELL row.
//
// The first version of this file put wireline and plug entries in the PER-WELL stream.
// 872 of 2,195 entries — 39.7%, every wireline run — therefore rendered on no band at
// all. An adversarial reviewer caught it; 32 passing tests did not. `seed.ts` carries
// the same defect and its header already names this fix: "seed only frac here and seed
// the shared services into the PAD stream".
//
// ── EVERYTHING HERE IS AUTHORED DEMO DATA ────────────────────────────────────────────
// Nothing is derived. Durations, stage counts and cadence are constants chosen to look
// like a 2018 Delaware Basin completion — NOT computed from rate, volume or proppant
// loading. Follows the mud-band precedent (DECISIONS.md 2026-09-03) and Charter Abs-2.
//
// ── WHAT THE PO RATIFIED (DECISIONS.md 2026-09-04) ───────────────────────────────────
//   • Stage pump time 2.5 h — "2-2.5 hours is defensible" / "that number is approved by me"
//   • Pad cadence ~6 stages/day — "6 stages a day back in 2018 is probably about right.
//     with swap on the zipper, safety meetings, etc."
//   • Zipper groups of 2-3 wells — "choose 2-3 well groups for the zipper"
//
// ONE GENERATOR (PLAN C): runs inside testDataGenerator.ts under Node at build time and
// the output lands in the database. Never a second duration model — the drilling clock
// stays oilfieldTimeLog.ts's and this module starts after the date that clock produced.
//
// PERSISTENCE: database, not localStorage (PO 2026-09-04).
//
// DATA MODEL: no DDL table yet — `stimulation_entry` is needed (pad → well → job →
//   entry). Tracked on features/stimulation/types.ts.
// UNIT MODEL: store-as-entered + UOM; depths authored in FEET with explicit
//   depthStartUnit/depthEndUnit (no SI normalization).

import {
  CODING_TEMPLATE_ID,
  CODING_TEMPLATE_VERSION,
  CRANE_LANE_ID,
  DEFAULT_STIMULATION_LANES,
  FRAC_LANE_ID,
  TCP_LANE_ID,
  isCriticalPathLane,
} from "../features/stimulation/constants";
import type {
  StimulationData,
  StimulationEntry,
} from "../features/stimulation/types";

const MS_PER_HOUR = 3_600_000;
const M_TO_FT = 3.28084;

const PUMPDOWN_LANE_ID = "lane-pumpdown";
const COIL_LANE_ID = "lane-coil";
const HSE_LANE_ID = "lane-hse";

/**
 * ZIPPER GROUPS — 2-3 wells (PO 2026-09-04, DECISIONS.md).
 *
 * Wells 0-2 and 3-5 are three-well zippers; 6-7 is a two-well zipper; 8 and 9 frac
 * alone and are deliberately given NO group id. Keeping two of them exercises the
 * non-SIMOPS path in the same corpus.
 *
 * The rule that a lone well is not a SIMOPS was cited here to `buildSimopsWellJobs.ts:8`, which
 * PJ-01 Slice 3 DELETED — the citation is repointed rather than left dangling, which is the
 * failure Gate G1 exists to catch. The rule now lives in `features/jobs/buildPadJobWellJobs.ts`,
 * and it CHANGED with the move: the deleted builder set a group id only when a fan-out covered
 * more than one well, whereas a PadJob IS the campaign, so a single-well stim pad job still gets
 * one and adding wells later joins it. This generator predates that and still models the old
 * rule — which is correct FOR DEMO DATA, since a lone well here is meant to render as a plain
 * single-well frac, not as a campaign of one.
 */
const ZIPPER_GROUPS: readonly (readonly number[])[] = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7],
];

/** Authored stage counts. NOT derived from lateral length. ~210-230 ft/stage on a
 *  1.83-mile lateral, which is where 2018 Delaware sat — the era ran 200-250 ft stages
 *  with 5-8 clusters, between early-2010s wide spacing and the 2020+ move to extreme
 *  limited entry. Conservative for 2024; correct for this corpus. */
const STAGE_COUNT: readonly number[] = [44, 46, 42, 45, 43, 47, 44, 41, 46, 43];

// ── The treatment: 2.5 h total — the stage pump time ratified by the PO on
//    2026-09-04 (docs/DECISIONS.md). ─────────────────────────────────────────────────
const BREAKDOWN_HOURS = 0.3; // acid spearhead + step-down. Every 2018 stage opens here.
const PAD_HOURS = 0.5; // pad — opens the fracture, no proppant
const RAMP_HOURS = 1.3; // proppant ramp — the concentration staircase
const FLUSH_HOURS = 0.4; // flush / displacement to the perfs
const TREATMENT_HOURS = BREAKDOWN_HOURS + PAD_HOURS + RAMP_HOURS + FLUSH_HOURS; // = 2.5

// ── The wireline run, which is what actually sets the pace ───────────────────────────
/**
 * THE BINDING CONSTRAINT, and the reason 2018 did six stages a day.
 *
 * An earlier version had wireline at 2.0 h — BELOW the 2.5 h pump — then held the
 * cadence to six with an anonymous `OPS_OVERHEAD_HOURS = 1.0`. An adversarial reviewer
 * pulled that apart: if wireline were genuinely faster than pumping AND genuinely hidden
 * behind it, the pad would run 9.6 stages/day, and only the fudge stopped it. The number
 * was right and the mechanism was invented.
 *
 * The real 2018 plug-and-perf turnaround — bleed off, rig up, pump down 10,000+ ft, set
 * the plug, shoot, pump out and POOH, rig down — is at or above pump time and therefore
 * ON the critical path. Putting the time here yields the same ratified cadence through a
 * mechanism that survives a domain question.
 */
const RIH_HOURS = 1.5;
const SET_PLUG_HOURS = 0.4;
const PERF_HOURS = 0.3;
const POOH_HOURS = 1.3;
const WIRELINE_HOURS = RIH_HOURS + SET_PLUG_HOURS + PERF_HOURS + POOH_HOURS; // = 3.5

/** Handoff between the wireline unit and the spread — bleed-off, valve line-up on the
 *  zipper manifold, the go-ahead. A zipper "swap" is a manifold change, not a rig move. */
const HANDOFF_HOURS = 0.5;

/** The pad's per-stage rhythm. Wireline-bound, which is the whole point above:
 *  3.5 + 0.5 = 4.0 h → 24 / 4.0 = SIX stages/day across a zipper group. */
const PAD_STAGE_HOURS = WIRELINE_HOURS + HANDOFF_HOURS;

/** Standoff left unperforated at each end of the lateral. Stage 1 must not shoot the
 *  shoe track and float equipment; the heel-most stage must not shoot at the landing
 *  point, in the curve. An earlier version had ZERO at both ends. */
const TOE_STANDOFF_FT = 250;
const HEEL_STANDOFF_FT = 300;

/** Days after the LAST well on the pad is down before frac crews arrive. */
const MOBILIZATION_DAYS = 21;

/** Days between zipper groups — one spread walks the pad, so a group cannot start until
 *  the previous is off it. A three-well group runs ~22 days at the ratified cadence. */
const GROUP_OFFSET_DAYS = 25;

export interface DemoStimulationArgs {
  padId: string;
  wellboreId: string;
  jobId: string;
  wellIndex: number;
  boreName: string;
  /** Landing point and total depth, METRES, from the generator's geometry. Used only to
   *  place perf depths — no engineering is derived from them. */
  landingMD: number;
  totalMD: number;
  padDrillingEndIso: string;
  nowIso: string;
}

export interface DemoStimulationResult {
  /** The "fracking" job to append to the well's jobs[]. */
  job: Record<string, unknown>;
  /** The WELL stream — `lane-frac` only, the one lane that renders on a well row. */
  data: StimulationData;
  /** This well's contribution to the PAD stream. The caller merges every well's
   *  contribution into ONE pad-scoped stream. */
  padEntries: StimulationEntry[];
  simopsGroupId: string | null;
}

export function zipperGroupFor(wellIndex: number): number | null {
  const idx = ZIPPER_GROUPS.findIndex((g) => g.includes(wellIndex));
  return idx === -1 ? null : idx;
}

function slotFor(wellIndex: number): number {
  const g = zipperGroupFor(wellIndex);
  if (g === null) return 0;
  const slot = ZIPPER_GROUPS[g]?.indexOf(wellIndex) ?? 0;
  return slot < 0 ? 0 : slot;
}

function groupStartDayFor(wellIndex: number): number {
  const g = zipperGroupFor(wellIndex);
  if (g !== null) return g * GROUP_OFFSET_DAYS;
  // Singles follow the groups. They are SLOWER per pad-day — no neighbour to work while
  // the wireline is in the hole — which is exactly why zippers exist.
  return 78 + (wellIndex - 8) * 14;
}

/** Deterministic 0..1 from two integers. S0 requires a rebuild to reproduce, so no
 *  Math.random anywhere — this is a hash, not entropy. */
function jitter(a: number, b: number): number {
  const h = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return h - Math.floor(h);
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

export function buildDemoStimulation(
  args: DemoStimulationArgs,
): DemoStimulationResult {
  const {
    padId,
    wellboreId,
    jobId,
    wellIndex,
    boreName,
    landingMD,
    totalMD,
    padDrillingEndIso,
    nowIso,
  } = args;

  const stages = STAGE_COUNT[wellIndex] ?? 44;
  const group = zipperGroupFor(wellIndex);
  const slot = slotFor(wellIndex);
  const groupSize = group === null ? 1 : (ZIPPER_GROUPS[group]?.length ?? 1);
  const simopsGroupId = group === null ? null : `simops-permian-${group + 1}`;

  // TWO DISTINCT RESOURCES — not the same crew, not the same contractor. An earlier
  // version tagged wireline runs with the frac spread's id, asserting the spread was
  // simultaneously pumping one well and running wireline on another: 1,016 overlapping
  // double-bookings, contradicting this module's own concurrency claim.
  const spreadId = "spread-permian";
  const wirelineId = "wireline-permian";
  const PUMPER = "Demo Pumping Services";
  const WIRELINE_CO = "Demo Wireline & Perforating";

  const campaignStartMs =
    Date.parse(padDrillingEndIso) + MOBILIZATION_DAYS * 24 * MS_PER_HOUR;
  const wellStartMs =
    campaignStartMs + groupStartDayFor(wellIndex) * 24 * MS_PER_HOUR;

  // A well's stages are spaced by the FULL group rotation: the wireline unit visits
  // every other well in the group before returning. A solo frac has no rotation —
  // wireline and pumping serialize on the same well, slower per PAD-day even though the
  // single well finishes sooner.
  const perStageHours =
    groupSize === 1
      ? WIRELINE_HOURS + HANDOFF_HOURS + TREATMENT_HOURS
      : groupSize * PAD_STAGE_HOURS;
  const slotOffsetHours = slot * PAD_STAGE_HOURS;

  // Perf envelope, with standoff at BOTH ends.
  const tdFt = totalMD * M_TO_FT;
  const landingFt = landingMD * M_TO_FT;
  const toeFt = tdFt - TOE_STANDOFF_FT;
  const heelFt = landingFt + HEEL_STANDOFF_FT;
  const perfSpanFt = Math.max(toeFt - heelFt, 1);
  const stepFt = perfSpanFt / stages;

  const wellEntries: StimulationEntry[] = [];
  const padEntries: StimulationEntry[] = [];
  let seq = 0;

  const entry = (
    into: StimulationEntry[],
    laneId: string,
    code1: string,
    startMs: number,
    hours: number,
    opts: {
      stageNumber?: number | null;
      depthFt?: [number, number];
      comment: string;
      contractor: string;
      resource: string | null;
      milestone?: boolean;
      isPad?: boolean;
    },
  ): void => {
    const [d0, d1] = opts.depthFt ?? [0, 0];
    into.push({
      id: `stim-${wellIndex}-${seq++}`,
      padId,
      // A PAD-scoped entry is stamped with the pad as its wellbore/job sentinel; the
      // well it services is `targetWellboreId` (types.ts:97-104, usePadServices.ts:98).
      wellboreId: opts.isPad ? padId : wellboreId,
      jobId: opts.isPad ? padId : jobId,
      laneId,
      // FROM THE MODULE'S OWN HELPER, never hardcoded. An earlier version set this by
      // hand and got it inverted on 100% of 872 entries — flagging a plug set as OFF the
      // critical path, when in plug-and-perf nothing proceeds until the plug is set.
      onCriticalPath: isCriticalPathLane(laneId),
      entryKind: opts.milestone ? "milestone" : "activity",
      startTs: iso(startMs),
      endTs: iso(startMs + (opts.milestone ? 0 : hours) * MS_PER_HOUR),
      code1,
      code2: "",
      stageNumber: opts.stageNumber ?? null,
      depthStart: d0.toFixed(0),
      depthStartUnit: "ft",
      depthEnd: d1.toFixed(0),
      depthEndUnit: "ft",
      party: "Permian Operations LLC",
      contractor: opts.contractor,
      attachments: [],
      sharedResourceId: opts.resource,
      ...(opts.isPad ? { targetWellboreId: wellboreId } : {}),
      isPlanned: false,
      codingTemplateId: CODING_TEMPLATE_ID,
      codingTemplateVersion: CODING_TEMPLATE_VERSION,
      comment: opts.comment,
    });
  };

  // ── Spread rig-up — ONCE PER GROUP, on the lead well only.
  //    Emitting it per well double-booked the spread: three overlapping rig-ups all
  //    tagged with the same resource. The spread rigs up when it arrives on the group,
  //    not each time it rotates to another well in it. ──
  if (slot === 0) {
    entry(
      padEntries,
      CRANE_LANE_ID,
      "SPREAD_RU",
      wellStartMs - 8 * MS_PER_HOUR,
      6,
      {
        comment:
          groupSize > 1
            ? `Rig up frac spread — zipper group of ${groupSize}`
            : `Rig up frac spread — ${boreName}`,
        contractor: PUMPER,
        resource: spreadId,
        isPad: true,
      },
    );
  }

  for (let s = 0; s < stages; s++) {
    const stageNumber = s + 1; // 1 = toe-most
    const btmFt = toeFt - s * stepFt;
    const topFt = toeFt - (s + 1) * stepFt;
    const depth: [number, number] = [topFt, btmFt];

    const stageStart =
      wellStartMs + (slotOffsetHours + s * perStageHours) * MS_PER_HOUR;

    // ── 1. Access the stage ──
    let t = stageStart;
    if (s === 0) {
      // The TOE cannot be reached by pumpdown wireline — there is no communication with
      // the formation yet. 2018 practice is a toe sleeve or TCP guns.
      entry(padEntries, TCP_LANE_ID, "RIH_TCP", t, 2.0, {
        stageNumber,
        depthFt: depth,
        comment: `Toe initiation — RIH TCP guns, ${boreName}`,
        contractor: WIRELINE_CO,
        resource: wirelineId,
        isPad: true,
      });
      t += 2.0 * MS_PER_HOUR;
      entry(padEntries, TCP_LANE_ID, "FIRE_TCP", t, 0.5, {
        stageNumber,
        depthFt: depth,
        comment: `Toe initiation — fire TCP, ${boreName}`,
        contractor: WIRELINE_CO,
        resource: wirelineId,
        isPad: true,
      });
      t += 1.0 * MS_PER_HOUR;
    } else {
      // Plug-and-perf on ONE wireline run: in, set the plug, shoot, out.
      entry(padEntries, PUMPDOWN_LANE_ID, "RIH_GUNS", t, RIH_HOURS, {
        stageNumber,
        depthFt: depth,
        comment: `Stage ${stageNumber} — pump down guns, ${boreName}`,
        contractor: WIRELINE_CO,
        resource: wirelineId,
        isPad: true,
      });
      t += RIH_HOURS * MS_PER_HOUR;
      entry(padEntries, PUMPDOWN_LANE_ID, "SET_PLUG", t, SET_PLUG_HOURS, {
        stageNumber,
        depthFt: [btmFt, btmFt],
        comment: `Stage ${stageNumber} — set composite plug, ${boreName}`,
        contractor: WIRELINE_CO,
        resource: wirelineId,
        isPad: true,
      });
      t += SET_PLUG_HOURS * MS_PER_HOUR;

      // A misrun happens. Roughly one stage in forty, deterministically placed.
      if (jitter(wellIndex, s) > 0.975) {
        entry(padEntries, PUMPDOWN_LANE_ID, "MISRUN", t, 1.2, {
          stageNumber,
          depthFt: depth,
          comment: `Stage ${stageNumber} — gun misfire, redress and re-run`,
          contractor: WIRELINE_CO,
          resource: wirelineId,
          isPad: true,
        });
        t += 1.2 * MS_PER_HOUR;
      }

      entry(padEntries, PUMPDOWN_LANE_ID, "PERF_RUN", t, PERF_HOURS, {
        stageNumber,
        depthFt: depth,
        comment: `Stage ${stageNumber} — perforate, ${boreName}`,
        contractor: WIRELINE_CO,
        resource: wirelineId,
        isPad: true,
      });
      t += PERF_HOURS * MS_PER_HOUR;
      entry(padEntries, PUMPDOWN_LANE_ID, "POOH_GUNS", t, POOH_HOURS, {
        stageNumber,
        depthFt: depth,
        comment: `Stage ${stageNumber} — POOH guns, ${boreName}`,
        contractor: WIRELINE_CO,
        resource: wirelineId,
        isPad: true,
      });
      t += POOH_HOURS * MS_PER_HOUR;
    }

    // ── 2. Safety meeting before pumping. Named in the PO's 2026-09-04 cadence ruling
    //       (docs/DECISIONS.md) as part of why 2018 ran six stages a day, so it is
    //       VISIBLE on the timeline rather than buried inside a constant. ──
    entry(padEntries, HSE_LANE_ID, "SAFETY_MEETING", t, 0.3, {
      stageNumber,
      comment: `Stage ${stageNumber} pre-job safety meeting`,
      contractor: PUMPER,
      resource: null,
      isPad: true,
    });
    const treatStart = t + HANDOFF_HOURS * MS_PER_HOUR;

    // ── 3. The treatment — the WELL band, the only lane that renders on a well row ──
    let f = treatStart;
    const frac = (code: string, hours: number, note: string): void => {
      entry(wellEntries, FRAC_LANE_ID, code, f, hours, {
        stageNumber,
        depthFt: depth,
        comment: `Stage ${stageNumber} — ${note}`,
        contractor: PUMPER,
        resource: spreadId,
      });
      f += hours * MS_PER_HOUR;
    };

    frac("BREAKDOWN", BREAKDOWN_HOURS, "acid spearhead / breakdown");
    frac("PAD", PAD_HOURS, "pad");

    // A screenout, roughly one stage in fifty. The stage is cut short — no flush.
    const screenedOut = jitter(wellIndex + 7, s) > 0.98;
    if (screenedOut) {
      frac("PROPPANT", RAMP_HOURS * 0.6, "proppant ramp");
      frac("SCREENOUT", 1.5, "SCREENOUT — stage terminated early");
    } else {
      frac("PROPPANT", RAMP_HOURS, "proppant ramp");
      frac("FLUSH", FLUSH_HOURS, "flush / displacement");
      entry(wellEntries, FRAC_LANE_ID, "ISIP", f, 0, {
        stageNumber,
        depthFt: depth,
        comment: `Stage ${stageNumber} — ISIP recorded`,
        contractor: PUMPER,
        resource: spreadId,
        milestone: true,
      });
    }

    // ── 4. The spread waits on wireline. LOGGED, not left as white space — this idle IS
    //       the 2018 story, and hiding it left half the earlier timeline blank. ──
    const nextStageStart =
      wellStartMs + (slotOffsetHours + (s + 1) * perStageHours) * MS_PER_HOUR;
    const idleHours = (nextStageStart - f) / MS_PER_HOUR;
    if (idleHours > 0.25 && s < stages - 1) {
      entry(wellEntries, FRAC_LANE_ID, "WAIT", f, idleHours, {
        stageNumber,
        comment: "Spread standing by — wireline on the offset well",
        contractor: PUMPER,
        // NOT the spread. This well is waiting precisely BECAUSE the spread is on a
        // neighbour; tagging the wait with the spread asserts it is occupied here and
        // there at once, which is the double-booking this module already made with the
        // wireline runs. A wait is a well state, not resource occupancy.
        resource: null,
      });
    }

    // ── 5. NO WEATHER ENTRIES, deliberately, and this is a gap worth naming.
    //       A Loving County campaign running mid-November to late February obviously
    //       loses time to freezing lines, and `lane-weather` sits pinned at the top of
    //       BOTH bands. But `STIMULATION_CODES` has NO entry for the weather lane — the
    //       lane ships with no code vocabulary at all. Authoring a `code1` here would
    //       invent a value the library does not define, which is the same class of
    //       defect as inventing a citation. Left out until the vocabulary exists.
  }

  const lastFrac = wellEntries.reduce(
    (acc, e) => (e.endTs > acc ? e.endTs : acc),
    wellEntries[0]?.startTs ?? iso(wellStartMs),
  );
  const afterMs = Date.parse(lastFrac);

  // ── Plug drillout + flowback. Composite plugs were set; somebody mills them. ──
  // Rig-down likewise belongs to the GROUP — but to the well that actually finishes
  // LAST, which is not the last slot: stage counts differ per well, so a later slot with
  // fewer stages can be off the pad while an earlier one is still pumping.
  const groupWells =
    group === null ? [wellIndex] : (ZIPPER_GROUPS[group] ?? []);
  const finishHoursFor = (w: number): number => {
    const st = STAGE_COUNT[w] ?? 44;
    const sl = slotFor(w);
    return sl * PAD_STAGE_HOURS + st * perStageHours;
  };
  const lastOut = groupWells.reduce(
    (best, w) => (finishHoursFor(w) > finishHoursFor(best) ? w : best),
    groupWells[0] ?? wellIndex,
  );
  if (wellIndex === lastOut) {
    entry(
      padEntries,
      CRANE_LANE_ID,
      "SPREAD_RD",
      afterMs + 4 * MS_PER_HOUR,
      5,
      {
        comment:
          groupSize > 1
            ? `Rig down frac spread — zipper group of ${groupSize}`
            : `Rig down frac spread — ${boreName}`,
        contractor: PUMPER,
        resource: spreadId,
        isPad: true,
      },
    );
  }
  entry(padEntries, COIL_LANE_ID, "CT_RU", afterMs + 12 * MS_PER_HOUR, 8, {
    comment: `Rig up coil — ${boreName}`,
    contractor: "Demo Coiled Tubing",
    resource: null,
    isPad: true,
  });
  entry(padEntries, COIL_LANE_ID, "DRILL_OUT", afterMs + 20 * MS_PER_HOUR, 34, {
    comment: `Drill out ${stages - 1} composite plugs — ${boreName}`,
    contractor: "Demo Coiled Tubing",
    resource: null,
    isPad: true,
  });
  // NO FLOWBACK ENTRY, and this is a gap rather than an omission.
  //
  // `lane-flowback` is in COMMON_LANE_IDS but NOT in COMMON_BAND_LANES — constants.ts:169
  // and :202 record it as PARKED after the C3 two-band collapse, so it renders on
  // neither band. An earlier version of this file emitted a 72 h FLOWBACK per well onto
  // it: ten entries written and displayed nowhere, which is the SAME class as the
  // 39.7%-invisible defect this module was rebuilt to fix, recurring in the same rebuild.
  //
  // The guard test could not see it because it used `isCommonLane` (= COMMON_LANE_IDS,
  // which includes parked lanes) rather than membership in the RENDERABLE set. Both are
  // corrected: the entry is gone and the test now asserts against COMMON_BAND_LANES.
  // Post-frac flowback belongs in the corpus once that lane is unparked.

  const allTimes = [...wellEntries, ...padEntries];
  const firstStart = allTimes.reduce(
    (acc, e) => (e.startTs < acc ? e.startTs : acc),
    allTimes[0]?.startTs ?? iso(wellStartMs),
  );
  const lastEnd = allTimes.reduce(
    (acc, e) => (e.endTs > acc ? e.endTs : acc),
    firstStart,
  );

  const job: Record<string, unknown> = {
    id: jobId,
    jobType: "stimulation",
    // Routes to the two-band stimulation screen. The prebake mapper pins ITS jobs to
    // "drilling" on purpose; this synthetic pad is the one that produces the SIMOPS
    // structures that screen expects.
    timelinePattern: "fracking",
    jobNumber: `Completion - ${boreName}`,
    status: "Completed",
    startDate: firstStart,
    endDate: lastEnd,
    ...(simopsGroupId ? { simopsGroupId } : {}),
    reports: [],
    createdAt: nowIso,
    modifiedAt: nowIso,
  };

  return {
    job,
    data: { entries: wellEntries, lanes: [...DEFAULT_STIMULATION_LANES] },
    padEntries,
    simopsGroupId,
  };
}
